import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { eventService } from '../services/eventService'
import { agendaService } from '../services/agendaService'
import { useAttendeeAuth } from './AttendeeAuthContext'
import { setTelemetryEvent, track } from '../services/telemetry'
import type { Attendee, EventInfo } from '../data/types'

function initialsOf(name: string): string {
  return (
    name
      .split(' ')
      .map((p) => p[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'VC'
  )
}

interface AppDataState {
  attendee: Attendee | null
  event: EventInfo | null
  loading: boolean
  savedSessionIds: Set<string>
  toggleSavedSession: (id: string) => void
  isSaved: (id: string) => boolean
}

const AppDataContext = createContext<AppDataState | undefined>(undefined)

const SAVED_KEY = 'vc-saved-sessions'

export function AppDataProvider({ children }: { children: ReactNode }) {
  const { me, isAuthenticated } = useAttendeeAuth()
  const [attendee, setAttendee] = useState<Attendee | null>(null)
  const [event, setEvent] = useState<EventInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [savedSessionIds, setSavedSessionIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(SAVED_KEY)
      return new Set<string>(raw ? JSON.parse(raw) : [])
    } catch {
      return new Set<string>()
    }
  })

  useEffect(() => {
    let active = true
    Promise.all([eventService.getAttendee(), eventService.getEvent()]).then(
      ([demoAttendee, e]) => {
        if (!active) return
        // Prefer the signed-in attendee's real identity; fall back to the demo
        // attendee when running in open/demo mode.
        if (isAuthenticated && me) {
          const name = me.fullName || me.email || 'Guest'
          setAttendee({
            name,
            firstName: name.split(' ')[0],
            email: me.email ?? '',
            company: me.company ?? '',
            role: '',
            track: me.track ?? '',
            initials: initialsOf(name),
          })
        } else {
          setAttendee(demoAttendee)
        }
        setEvent(e)
        setLoading(false)
        // Scope telemetry to this event and record an app open (once).
        setTelemetryEvent(e.id)
        track('app_open')
      },
    )
    return () => {
      active = false
    }
  }, [isAuthenticated, me])

  // When signed in, the personal agenda is persisted server-side (durable +
  // shared across devices). Load it on auth; on failure, keep local state.
  // In demo/open mode we fall back to localStorage (seeded from mock flags).
  useEffect(() => {
    let active = true
    if (isAuthenticated) {
      agendaService
        .getSavedIds()
        .then((ids) => {
          if (active) setSavedSessionIds(new Set(ids))
        })
        .catch(() => {
          /* keep whatever we have (offline / not yet reachable) */
        })
      return () => {
        active = false
      }
    }
    // Demo mode: seed defaults from mock "saved" flags the first time.
    if (localStorage.getItem(SAVED_KEY)) return
    eventService.getSessions().then((sessions) => {
      if (!active) return
      const seeded = new Set(sessions.filter((s) => s.saved).map((s) => s.id))
      setSavedSessionIds(seeded)
    })
    return () => {
      active = false
    }
  }, [isAuthenticated])

  // Mirror to localStorage as an offline cache / demo-mode store.
  useEffect(() => {
    localStorage.setItem(SAVED_KEY, JSON.stringify([...savedSessionIds]))
  }, [savedSessionIds])

  const toggleSavedSession = useCallback(
    (id: string) => {
      let willSave = false
      setSavedSessionIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) {
          next.delete(id)
          willSave = false
        } else {
          next.add(id)
          willSave = true
        }
        return next
      })
      // Persist server-side when signed in (optimistic; revert on failure).
      if (isAuthenticated) {
        const op = willSave
          ? agendaService.save(id)
          : agendaService.unsave(id)
        op.catch(() => {
          setSavedSessionIds((prev) => {
            const next = new Set(prev)
            if (willSave) next.delete(id)
            else next.add(id)
            return next
          })
        })
      }
    },
    [isAuthenticated],
  )

  const value = useMemo<AppDataState>(
    () => ({
      attendee,
      event,
      loading,
      savedSessionIds,
      toggleSavedSession,
      isSaved: (id: string) => savedSessionIds.has(id),
    }),
    [attendee, event, loading, savedSessionIds, toggleSavedSession],
  )

  return (
    <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAppData() {
  const ctx = useContext(AppDataContext)
  if (!ctx) throw new Error('useAppData must be used within AppDataProvider')
  return ctx
}

/**
 * Like useAppData but returns null instead of throwing when there is no
 * AppDataProvider (e.g. a component reused in the admin page-builder preview,
 * which renders attendee blocks without the attendee data context). Callers
 * must handle the null case (typically by hiding attendee-only affordances).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalAppData() {
  return useContext(AppDataContext)
}
